import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { TrendingUpIcon, TrendingDownIcon, DollarSign, Users, Building2, Warehouse } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/ui/loading-state";

interface MetricCardProps {
  title: string;
  value: string;
  change: number;
  icon: React.ReactNode;
}

const getMetricIcon = (title: string) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('revenue')) return <DollarSign className="w-6 h-6 text-primary" />;
  if (lowerTitle.includes('customers')) return <Users className="w-6 h-6 text-primary" />;
  if (lowerTitle.includes('project size')) return <Building2 className="w-6 h-6 text-primary" />;
  if (lowerTitle.includes('pipeline')) return <Warehouse className="w-6 h-6 text-primary" />;
  return <TrendingUpIcon className="w-6 h-6 text-primary" />;
};

const getMetricChangeText = (title: string, change: number) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes('revenue')) return 'Ave over the course of 3 years';
  if (lowerTitle.includes('customers')) return 'Ops/Micro/Mega';
  if (lowerTitle.includes('project size')) return 'Preopening & Renovation Average Size';
  if (lowerTitle.includes('pipeline')) return 'in pipeline value';
  return 'from last month';
};

const MetricCard = ({ title, value, change, icon }: MetricCardProps) => (
  <Card>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <h3 className="text-2xl font-bold mt-2">{value}</h3>
        </div>
        <div className={`p-3 rounded-full ${change >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
          {icon}
        </div>
      </div>
      <div className="flex items-center mt-4">
        <span className={`text-sm font-medium ${change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
          {getMetricChangeText(title, change)}
        </span>
      </div>
    </CardContent>
  </Card>
);

export const KeyMetrics = () => {
  const { data: metrics, isLoading, error } = useQuery({
    queryKey: ['financial-metrics'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('financial_metrics')
        .select('*');
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) return <LoadingState />;
  if (error) return <div>Error loading metrics</div>;
  if (!metrics?.length) return <div>No metrics available</div>;

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-6">Key Metrics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <MetricCard
            key={metric.id}
            title={metric.title}
            value={metric.value}
            change={metric.change_percentage}
            icon={getMetricIcon(metric.title)}
          />
        ))}
      </div>
    </div>
  );
};